import { Container, Title, Text, Button, Group, Stack, Box, Paper, useMantineTheme, Image, SimpleGrid, ThemeIcon, Badge, Accordion, Avatar, Rating } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconHeartFilled, IconCalendarEvent, IconMedicalCross, IconClock, IconChevronRight, IconClipboardCheck, IconArrowUp, IconMapPin, IconUser } from '@tabler/icons-react';
import { useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { SEOHead } from '../components/SEOHead';
import { FEATURE_TOGGLES } from '../utils/featureToggles';

interface AvailableSlot {
  date: string;
  time: string;
  formattedDate: string;
}

interface Testimonial {
  id: number;
  name: string;
  rating: number;
  text: string;
  treatment: string;
  avatar?: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Anonym',
    rating: 5,
    text: 'Jeg har haft den mest fantastiske oplevelse hos akupunktør Pernille. Hun er utrolig kompetent og dygtig i sit fag, hvilket gør, at man føler sig i trygge hænder fra første øjeblik. Pernille er altid glad, smilende, og imødekommende, hvilket skaber en varm og behagelig atmosfære i klinikken. Hun har en dyb forståelse for sine patienters behov og er meget fleksibel i forhold til at tilpasse behandlingen, så den passer perfekt til ens individuelle situation. Pernille er ambitiøs og stræber konstant efter at forbedre sine færdigheder og tilbyde den bedst mulige behandling. Jeg kan varmt anbefale Pernille til alle, der søger en professionel og ambitiøs akupunktør',
    treatment: 'Akupunktur'
  },
  {
    id: 2,
    name: 'Anonym',
    rating: 5,
    text: 'Jeg kontaktede Lykke Terapien - Akupunkturklinik i Horsens for ca tre måneder siden. På dette tidspunkt, var jeg udmattet, konstant træt, energi forladt, jeg havde svært ved at få luft, (jeg har tre lunge lidelser) jeg havde tanke mylder, søvnproblemer, svært ved at koncentrere mig, jeg havde smerter af forskellig slags i min fod, efter en nerveskade. Istedet for at skulle have endnu mere medicin, for mine lungelidelser, valgte jeg at give akupunktur hos Lykke Terapien en chance. Hvilket jeg absolut ikke har fortrudt. Jeg kan kun varmt anbefale Lykke Terapi, Pernilles fantastiske måde, at være opmærksom, lyttende og hendes behandling er helt unikt. Jeg har fået det så meget bedre og er Pernille meget taknemmelig.',
    treatment: 'Akupunktur'
  },
  {
    id: 3,
    name: 'Anonym',
    rating: 5,
    text: 'Stille, roligt og trygt at komme i klinikken, Pernille er meget lydhør over for de udfordringer jeg har/havde og laver behandling efter det. Har og har haft stor effekt af behandlingen. De bedste anbefalinger til Pernille herfra 🦋',
    treatment: 'Akupunktur'
  }
];

const processSteps = [
  {
    icon: IconCalendarEvent,
    title: 'Book tid online',
    description: 'Vælg en tid der passer dig',
    color: 'gold',
    clickable: true,
    link: 'https://lykke-terapien.planway.com/'
  },
  {
    icon: IconClipboardCheck,
    title: 'Få bekræftelse',
    description: 'Modtag bekræftelse på email',
    color: 'green',
    clickable: false
  },
  {
    icon: IconMapPin,
    title: 'Mød op i klinikken',
    description: 'Vi ses til din behandling',
    color: 'blue',
    clickable: false
  }
];

const faqData = [
  {
    question: 'Hvad kan akupunktur hjælpe med?',
    answer: 'Akupunktur kan hjælpe med mange forskellige tilstande som rygsmerter, hovedpine, stress, søvnproblemer, fordøjelsesbesvær, og meget mere. Behandlingen er helhedsorienteret og fokuserer på at genoprette kroppens naturlige balance.'
  },
  {
    question: 'Hvor lang tid tager en behandling?',
    answer: 'En første konsultation tager typisk 60-90 minutter, hvor vi går grundigt igennem din historie og dine behov. Efterfølgende behandlinger tager normalt 45-60 minutter.'
  },
  {
    question: 'Gør akupunktur ondt?',
    answer: 'De fleste oplever en strømmende fornemmelse eller et lille jag, når nålene sættes. Nålene er meget tynde, og mange beskriver fornemmelsen som en let prik eller slet ikke mærker dem. Under behandlingen føler mange sig afslappede.'
  },
  {
    question: 'Hvor mange behandlinger har jeg brug for?',
    answer: 'Det afhænger af din specifikke situation. Nogle mærker forbedring efter få behandlinger, mens andre har brug for et længere behandlingsforløb. Vi laver altid en individuel plan sammen.'
  },
  {
    question: 'Hvad koster en behandling?',
    answer: 'Priser varierer afhængigt af behandlingstype og varighed. Kontakt mig for aktuelle priser og eventuelle tilbud for behandlingsforløb.'
  }
];

export function HomePage() {
  const theme = useMantineTheme();
  const testimonialsEnabled = FEATURE_TOGGLES.HOMEPAGE_TESTIMONIALS;
  const [isVisible, setIsVisible] = useState({
    hero: false,
    process: false,
    faq: false,
    booking: false,
    treatment: false,
    testimonials: false
  });
  const [sectionsLoaded, setSectionsLoaded] = useState({
    faq: false,
    process: false,
    treatment: false
  });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [expandedTestimonials, setExpandedTestimonials] = useState<Set<number>>(new Set());

  const structuredData = useMemo(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    };

    if (!testimonialsEnabled) {
      return [faqSchema];
    }

    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Lykke Terapien',
      url: 'https://www.lykketerapien.dk/',
      image: 'https://www.lykketerapien.dk/logo.png',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: testimonials.length.toString()
      },
      review: testimonials.map((testimonial) => ({
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: testimonial.name
        },
        reviewBody: testimonial.text,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: testimonial.rating,
          bestRating: '5'
        },
        itemReviewed: {
          '@type': 'Service',
          name: testimonial.treatment
        }
      }))
    };

    return [faqSchema, reviewSchema];
  }, [testimonialsEnabled]);


  // Staggered section loading for better UX
  useEffect(() => {
    const loadSections = async () => {
      // Load process steps
      setTimeout(() => setSectionsLoaded(prev => ({ ...prev, process: true })), 300);
      // Load FAQ
      setTimeout(() => setSectionsLoaded(prev => ({ ...prev, faq: true })), 500);
      // Load treatment
      setTimeout(() => setSectionsLoaded(prev => ({ ...prev, treatment: true })), 700);
    };
    
    loadSections();
  }, []);

  // Scroll animation trigger
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          const section = target.dataset.section as keyof typeof isVisible;
          if (section) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => observer.observe(section));

    // Scroll to top button visibility
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleTestimonial = (id: number) => {
    setExpandedTestimonials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <>
      <SEOHead
        title="Akupunktur klinik i Horsens | Book tid - Lykke Terapien"
        description="Professionel akupunktur i Horsens. Oplev behandling i rolige omgivelser med fokus på balance, energi og velvære. Book tid i dag."
        canonical="/"
        structuredData={structuredData}
      />
      <Box style={{ overflow: 'hidden' }}>
        {/* Floating particles background */}
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: -1,
          background: `
            radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 123, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255, 215, 0, 0.05) 0%, transparent 50%)
          `
        }}
      />

      {/* Hero Section - Improved Mobile */}
      <Box
        h={{ base: 'auto', sm: 'calc(100vh - 60px)' }}
        mih={{ base: 'calc(100vh - 80px)', sm: 'auto' }}
        display="flex"
        data-section="hero"
        style={{
          alignItems: 'center',
          background: `linear-gradient(135deg, ${theme.colors.gold[0]} 0%, ${theme.colors.blue[0]} 100%)`,
          position: 'relative'
        }}
        py={{ base: 'xl', sm: 0 }}
      >
        <Container size="lg" mx="auto">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            {/* Left Column - Content */}
            <Stack 
              gap="xl"
              style={{
                transform: isVisible.hero ? 'translateX(0)' : 'translateX(-50px)',
                opacity: isVisible.hero ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              <Box>
                <Badge 
                  size="lg"
                  variant="light" 
                  color="gold" 
                  mb="md"
                  leftSection={<IconMedicalCross size={16} />}
                  style={{
                    transform: isVisible.hero ? 'scale(1)' : 'scale(0.8)',
                    transition: 'transform 0.6s ease-out 0.2s'
                  }}
                >
                  Akupunktur & Terapi
                </Badge>
                <Title order={1} size="3rem" lh={1.2} mb="md" className="hero-title">
                  <Text span c={theme.colors.gold[7]} inherit>
                    Lykke Terapien
                  </Text>
                  <br />
                  <Text span c={theme.colors.dark[7]} inherit size="2.2rem" className="hero-subtitle">
                    Akupunkturklinik i Horsens
                  </Text>
                </Title>
                <Text size="xl" c="gray.7" lh={1.6} mb="xl" className="hero-description">
                  Oplev akupunktur i rolige omgivelser. 
                  Behandling med fokus på din individuelle vej til balance og velvære.
                </Text>
              </Box>

              <Group gap="md">
                <Stack w="100%" gap="sm" className="mobile-buttons" hiddenFrom="sm">
                  <Button 
                    component="a"
                    href="https://lykke-terapien.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="lg" 
                    radius="md"
                    fullWidth
                    leftSection={<IconCalendarEvent size={20} />}
                    style={{ 
                      boxShadow: theme.shadows.md,
                      transform: isVisible.hero ? 'translateY(0)' : 'translateY(20px)',
                      opacity: isVisible.hero ? 1 : 0,
                      transition: 'all 0.6s ease-out 0.4s'
                    }}
                  >
                    Book tid
                  </Button>
                  <Button 
                    component={Link} 
                    to="/om-mig" 
                    variant="light" 
                    size="lg" 
                    radius="md"
                    fullWidth
                    leftSection={<IconHeartFilled size={20} />}
                    style={{
                      transform: isVisible.hero ? 'translateY(0)' : 'translateY(20px)',
                      opacity: isVisible.hero ? 1 : 0,
                      transition: 'all 0.6s ease-out 0.6s'
                    }}
                  >
                    Læs mere
                  </Button>
                </Stack>
                
                <Group gap="md" className="desktop-buttons" visibleFrom="sm">
                  <Button 
                    component="a"
                    href="https://lykke-terapien.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xl" 
                    radius="md"
                    leftSection={<IconCalendarEvent size={20} />}
                    style={{ 
                      boxShadow: theme.shadows.md,
                      transform: isVisible.hero ? 'translateY(0)' : 'translateY(20px)',
                      opacity: isVisible.hero ? 1 : 0,
                      transition: 'all 0.6s ease-out 0.4s'
                    }}
                  >
                    Book tid
                  </Button>
                  <Button 
                    component={Link} 
                    to="/om-mig" 
                    variant="light" 
                    size="xl" 
                    radius="md"
                    leftSection={<IconHeartFilled size={20} />}
                    style={{
                      transform: isVisible.hero ? 'translateY(0)' : 'translateY(20px)',
                      opacity: isVisible.hero ? 1 : 0,
                      transition: 'all 0.6s ease-out 0.6s'
                    }}
                  >
                    Læs mere
                  </Button>
                </Group>
              </Group>
            </Stack>

            {/* Right Column - Logo */}
            <Box 
              ta="center"
              style={{
                transform: isVisible.hero ? 'translateX(0) scale(1)' : 'translateX(50px) scale(0.9)',
                opacity: isVisible.hero ? 1 : 0,
                transition: 'all 0.8s ease-out 0.3s'
              }}
            >
              <Image 
                src="/logo.png" 
                alt="Lykke Terapien Logo" 
                maw={400}
                mx="auto"
                className="hero-logo"
                style={{ 
                  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))',
                  animation: 'float 6s ease-in-out infinite'
                }}
              />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>


      {/* Process Steps Section - New! */}
      <Container size="lg" py="xl" data-section="process">
        <Box 
          ta="center" 
          mb="xl"
          style={{
            transform: isVisible.process ? 'translateY(0)' : 'translateY(30px)',
            opacity: isVisible.process ? 1 : 0,
            transition: 'all 0.8s ease-out'
          }}
        >
          <Title order={2} mb="md" c={theme.colors.dark[7]} size="xl">
            Sådan kommer du i gang
          </Title>
          <Text size="lg" c="gray.6" maw={600} mx="auto">
            Tre enkle skridt til din første behandling
          </Text>
        </Box>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl">
          {processSteps.map((step, index) => {
            const content = (
              <Stack 
                align="center" 
                ta="center" 
                gap="md"
                style={{
                  transform: isVisible.process ? 'translateY(0)' : 'translateY(30px)',
                  opacity: isVisible.process ? 1 : 0,
                  transition: `all 0.6s ease-out ${index * 0.2}s`,
                  cursor: step.clickable ? 'pointer' : 'default'
                }}
                className={step.clickable ? 'hover-lift' : ''}
              >
                <ThemeIcon size={80} radius="50%" color={step.color} variant="light" className="process-icon">
                  <step.icon size={35} />
                </ThemeIcon>
                <Box>
                  <Text fw={600} size="lg" mb="xs">
                    {step.title}
                  </Text>
                  <Text size="md" c="dimmed">
                    {step.description}
                  </Text>
                </Box>
                {index < processSteps.length - 1 && (
                  <IconChevronRight 
                    size={20} 
                    style={{ 
                      color: theme.colors.gray[4],
                      transform: 'rotate(90deg)',
                      display: index === processSteps.length - 1 ? 'none' : 'block'
                    }} 
                    className="process-arrow visibleFrom-md"
                  />
                )}
              </Stack>
            );

            return step.clickable ? (
              step.link!.startsWith('http') ? (
                <a key={index} href={step.link!} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                  {content}
                </a>
              ) : (
                <Link key={index} to={step.link!} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {content}
                </Link>
              )
            ) : (
              <div key={index}>
                {content}
              </div>
            );
          })}
        </SimpleGrid>
      </Container>

      {/* FAQ Section - New! */}
      <Box style={{ background: 'rgba(255, 248, 220, 0.2)' }} py="xl" data-section="faq">
        <Container size="lg">
          <Box
            ta="center"
            mb="xl"
            style={{
              transform: isVisible.faq ? 'translateY(0)' : 'translateY(30px)',
              opacity: isVisible.faq ? 1 : 0,
              transition: 'all 0.8s ease-out'
            }}
          >
            <Title order={2} c={theme.colors.dark[7]} mb="md" size="xl">
              Ofte stillede spørgsmål
            </Title>
            <Text size="lg" c="gray.6">
              Find svar på de mest almindelige spørgsmål om akupunktur
            </Text>
          </Box>

          <Box
            maw={800}
            mx="auto"
            style={{
              transform: isVisible.faq ? 'scale(1)' : 'scale(0.95)',
              opacity: isVisible.faq ? 1 : 0,
              transition: 'all 0.8s ease-out 0.3s'
            }}
          >
            <Accordion variant="separated" radius="md">
              {faqData.map((item, index) => (
                <Accordion.Item key={index} value={index.toString()}>
                  <Accordion.Control>
                    <Text fw={500} size="md">
                      {item.question}
                    </Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text size="md" lh={1.6}>
                      {item.answer}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        </Container>
      </Box>

      {testimonialsEnabled && (
        <>
          {/* Testimonials Section */}
          <Box py="xl" data-section="testimonials">
            <Container size="lg">
              <Stack
                gap="xs"
                align="center"
                mb="xl"
                style={{
                  transform: isVisible.testimonials ? 'translateY(0)' : 'translateY(30px)',
                  opacity: isVisible.testimonials ? 1 : 0,
                  transition: 'all 0.8s ease-out'
                }}
              >
                <Badge size="lg" variant="light" color="gold">
                  Kundeudtalelser
                </Badge>
                <Title order={2} c={theme.colors.dark[7]} size="xl">
                  Oplev hvad klienterne siger
                </Title>
                <Text size="lg" c="gray.6" ta="center" maw={600}>
                  Ærlige ord fra klienter der har fået behandling hos Pernille
                </Text>
              </Stack>

              {/* Desktop: Side by side grid */}
              <Box visibleFrom="sm">
                <SimpleGrid cols={{ base: 2, lg: 3 }} spacing="xl">
                  {testimonials.map((testimonial, index) => (
                    <Paper
                      key={testimonial.id}
                      radius="lg"
                      shadow="md"
                      p="xl"
                      withBorder
                      style={{
                        height: '100%',
                        transform: isVisible.testimonials ? 'translateY(0)' : 'translateY(30px)',
                        opacity: isVisible.testimonials ? 1 : 0,
                        transition: `all 0.6s ease-out ${index * 0.1}s`
                      }}
                    >
                      <Stack gap="md" h="100%">
                        <Group gap="md">
                          <Avatar radius="xl" size={60} color="gold">
                            <IconUser size={30} />
                          </Avatar>
                          <Stack gap={0}>
                            <Text fw={600}>{testimonial.name}</Text>
                            <Text size="sm" c="dimmed">
                              {testimonial.treatment}
                            </Text>
                          </Stack>
                        </Group>
                        <Rating value={testimonial.rating} readOnly size="sm" color="gold" />
                        <Text size="md" c="gray.7" lh={1.6}>
                          "{testimonial.text}"
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </Box>

              {/* Mobile: Swipeable carousel */}
              <Box hiddenFrom="sm" className="testimonials-carousel">
                {testimonials.map((testimonial, index) => {
                  const isExpanded = expandedTestimonials.has(testimonial.id);
                  const shouldTruncate = testimonial.text.length > 150;

                  return (
                    <Paper
                      key={testimonial.id}
                      radius="lg"
                      shadow="md"
                      p="xl"
                      withBorder
                      className="testimonial-card"
                      style={{
                        transform: isVisible.testimonials ? 'translateY(0)' : 'translateY(30px)',
                        opacity: isVisible.testimonials ? 1 : 0,
                        transition: `all 0.6s ease-out ${index * 0.1}s`
                      }}
                    >
                      <Stack gap="md">
                        <Group gap="md">
                          <Avatar radius="xl" size={60} color="gold">
                            <IconUser size={30} />
                          </Avatar>
                          <Stack gap={0}>
                            <Text fw={600}>{testimonial.name}</Text>
                            <Text size="sm" c="dimmed">
                              {testimonial.treatment}
                            </Text>
                          </Stack>
                        </Group>
                        <Rating value={testimonial.rating} readOnly size="sm" color="gold" />
                        <Text size="md" c="gray.7" lh={1.6}>
                          "{isExpanded ? testimonial.text : truncateText(testimonial.text)}"
                        </Text>
                        {shouldTruncate && (
                          <Button
                            variant="subtle"
                            size="xs"
                            color="gold"
                            onClick={() => toggleTestimonial(testimonial.id)}
                          >
                            {isExpanded ? 'Læs mindre' : 'Læs mere'}
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            </Container>
          </Box>
        </>
      )}

      {/* Treatment Preview Section - Improved Mobile */}
      <Box style={{ background: 'rgba(255, 248, 220, 0.3)' }} py="xl" data-section="treatment">
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box
              style={{
                transform: isVisible.treatment ? 'translateX(0)' : 'translateX(-50px)',
                opacity: isVisible.treatment ? 1 : 0,
                transition: 'all 0.8s ease-out'
              }}
            >
              <Title order={2} mb="md" c={theme.colors.dark[7]} size="xl">
                Helhedsorienteret Akupunktur
              </Title>
              <Text size="lg" mb="md" lh={1.6}>
                Behandling baseret på klassisk kinesisk medicin, hvor kroppen ses som en helhed. 
                Jeg fokuserer ikke kun på symptomer, men også på de bagvedliggende årsager.
              </Text>
              <Stack gap="sm" mb="xl">
                {[
                  { icon: IconClock, text: 'Individuel behandlingstid' },
                  { icon: IconHeartFilled, text: 'Fokus på din unikke situation' },
                  { icon: IconMedicalCross, text: 'Evidensbaseret praksis' }
                ].map((item, index) => (
                  <Group 
                    key={index}
                    gap="xs"
                    style={{
                      transform: isVisible.treatment ? 'translateX(0)' : 'translateX(-20px)',
                      opacity: isVisible.treatment ? 1 : 0,
                      transition: `all 0.6s ease-out ${0.2 + index * 0.1}s`
                    }}
                  >
                    <ThemeIcon size="sm" color="gold" variant="light">
                      <item.icon size={12} />
                    </ThemeIcon>
                    <Text size="md">{item.text}</Text>
                  </Group>
                ))}
              </Stack>
              <Button 
                component={Link} 
                to="/behandlinger" 
                variant="light" 
                size="lg"
                leftSection={<IconMedicalCross size={18} />}
                className="treatment-button"
                style={{
                  transform: isVisible.treatment ? 'translateY(0)' : 'translateY(20px)',
                  opacity: isVisible.treatment ? 1 : 0,
                  transition: 'all 0.6s ease-out 0.5s'
                }}
              >
                Se alle behandlinger
              </Button>
            </Box>
            
            <SimpleGrid 
              cols={2} 
              spacing="md"
              style={{
                transform: isVisible.treatment ? 'translateX(0)' : 'translateX(50px)',
                opacity: isVisible.treatment ? 1 : 0,
                transition: 'all 0.8s ease-out 0.3s'
              }}
            >
              <Image 
                src="/needleTherapy.jpg" 
                alt="Akupunktur behandling"
                radius="md"
                h={200}
                fit="cover"
                className="treatment-image"
                style={{ 
                  boxShadow: theme.shadows.sm,
                  ':hover': { transform: 'scale(1.05)' },
                  transition: 'transform 0.3s ease'
                }}
              />
              <Image 
                src="/øre.png" 
                alt="Øreakupunktur"
                radius="md"
                h={200}
                fit="cover"
                className="treatment-image"
                style={{ 
                  boxShadow: theme.shadows.sm,
                  ':hover': { transform: 'scale(1.05)' },
                  transition: 'transform 0.3s ease'
                }}
              />
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>


      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .hover-lift {
          transition: all 0.3s ease;
        }
        
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }

        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          /* Hero section improvements */
          .hero-title {
            font-size: 1.75rem !important;
            line-height: 1.3 !important;
            margin-bottom: 0.5rem !important;
          }
          
          .hero-subtitle {
            font-size: 1.25rem !important;
            line-height: 1.4 !important;
          }
          
          .hero-description {
            font-size: 0.95rem !important;
            margin-bottom: 1.5rem !important;
            line-height: 1.5 !important;
          }
          
          .hero-disclaimer {
            text-align: center !important;
            font-size: 0.875rem !important;
          }
          
          .hero-logo {
            max-width: 280px !important;
            margin-top: 1rem !important;
          }
          
          /* Button improvements */
          .mantine-Button-root {
            min-height: 48px !important;
            font-size: 1rem !important;
            padding: 0.75rem 1rem !important;
          }
          
          .mobile-buttons {
            width: 100% !important;
            max-width: 320px !important;
            margin: 0 auto !important;
          }
          
          
          /* Process section improvements */
          .process-icon {
            width: 60px !important;
            height: 60px !important;
          }
          
          .process-arrow {
            display: none !important;
          }
          
          /* Booking cards improvements */
          .mantine-Card-root {
            min-height: auto !important;
            padding: 1rem !important;
          }
          
          /* FAQ section improvements */
          .mantine-Accordion-control {
            padding: 1rem !important;
            font-size: 0.9rem !important;
          }
          
          .mantine-Accordion-panel {
            padding: 1rem !important;
            font-size: 0.875rem !important;
          }
          
          /* Treatment section improvements */
          .treatment-image {
            height: 160px !important;
          }
          
          .treatment-button {
            width: 100% !important;
          }
          
          /* CTA section improvements */
          .mantine-Paper-root {
            padding: 1.5rem !important;
          }
          
          /* Container spacing improvements */
          .mantine-Container-root:not(footer .mantine-Container-root) {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          /* Section spacing improvements */
          .mantine-Box-root[data-section] {
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
          }

          /* Title improvements - exclude footer */
          .mantine-Title-root:not(footer .mantine-Title-root):not(footer *) {
            text-align: center !important;
          }

          /* Text improvements - exclude footer */
          .mantine-Text-root:not(footer .mantine-Text-root):not(footer *) {
            text-align: center !important;
          }

          /* Grid improvements - exclude footer */
          .mantine-SimpleGrid-root:not(footer .mantine-SimpleGrid-root):not(footer *) {
            gap: 1rem !important;
          }

          /* Stack improvements - exclude footer */
          .mantine-Stack-root:not(footer .mantine-Stack-root):not(footer *) {
            gap: 1rem !important;
          }

          /* Group improvements - exclude footer */
          .mantine-Group-root:not(footer .mantine-Group-root):not(footer *) {
            gap: 0.75rem !important;
          }
          
          /* Floating buttons mobile positioning */
          .floating-book-button {
            bottom: 90px !important;
            left: 1rem !important;
            min-width: 120px !important;
            height: 48px !important;
            font-size: 0.875rem !important;
          }

          .floating-scroll-button {
            bottom: 1.5rem !important;
            right: 1.5rem !important;
            width: 48px !important;
            height: 48px !important;
          }
        }

        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .hero-title {
            font-size: 2.5rem !important;
          }
          
          .hero-subtitle {
            font-size: 1.75rem !important;
          }
          
          .hero-description {
            font-size: 1.125rem !important;
          }
          
          .hero-logo {
            max-width: 350px !important;
          }
          
          .process-icon {
            width: 70px !important;
            height: 70px !important;
          }
          
          .treatment-image {
            height: 180px !important;
          }
          
          .mantine-Container-root {
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
          }
        }

        /* Desktop button visibility */
        @media (min-width: 769px) {
          .mobile-buttons {
            display: none !important;
          }
          
        }

        /* Mobile button visibility */
        @media (max-width: 768px) {
          .desktop-buttons {
            display: none !important;
          }
          
        }
        
        /* Enhanced touch interactions */
        @media (max-width: 768px) {
          .mantine-Card-root {
            transition: all 0.3s ease !important;
          }
          
          .mantine-Card-root:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
          }
          
          .mantine-Button-root:hover {
            transform: translateY(-1px) !important;
          }
          
          .hover-lift:hover {
            transform: translateY(-3px) !important;
            box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
          }
        }
        
        /* Floating button animations */
        .floating-book-button:hover {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% { transform: translateY(-2px) scale(1.05); }
          50% { transform: translateY(-4px) scale(1.08); }
          100% { transform: translateY(-2px) scale(1.05); }
        }
        
        /* Improved loading states */
        .mantine-Skeleton-root {
          border-radius: 8px !important;
        }
        
        /* Enhanced visual hierarchy */
        @media (max-width: 768px) {
          .mantine-Title-root[data-order="1"] {
            font-size: 1.75rem !important;
            line-height: 1.3 !important;
          }
          
          .mantine-Title-root[data-order="2"] {
            font-size: 1.5rem !important;
            line-height: 1.4 !important;
          }
          
          .mantine-Title-root[data-order="3"] {
            font-size: 1.25rem !important;
            line-height: 1.4 !important;
          }
        }
        
        /* Improved accessibility */
        @media (max-width: 768px) {
          .mantine-ThemeIcon-root:not(footer .mantine-ThemeIcon-root):not(footer *) {
            min-width: 44px !important;
            min-height: 44px !important;
          }

          .mantine-Anchor-root:not(footer .mantine-Anchor-root):not(footer *) {
            min-height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
        }
        
        /* Better spacing for mobile */
        @media (max-width: 768px) {
          .mantine-Container-root[data-size="lg"] {
            max-width: 100% !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
          
          .mantine-Container-root[data-size="sm"] {
            max-width: 100% !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
        }

        /* Alert improvements */
        @media (max-width: 768px) {
          .mantine-Alert-root {
            padding: 1rem !important;
            font-size: 0.875rem !important;
          }
        }
        
        /* Paper improvements */
        @media (max-width: 768px) {
          .mantine-Paper-root {
            padding: 1rem !important;
            margin: 0.5rem 0 !important;
          }
        }
        
        /* Improved image responsiveness */
        @media (max-width: 768px) {
          .mantine-Image-root {
            width: 100% !important;
            max-width: 100% !important;
          }
        }
        
        /* Testimonials carousel for mobile */
        .testimonials-carousel {
          display: flex;
          gap: 1rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none; /* Firefox */
          padding-bottom: 1rem;
        }

        .testimonials-carousel::-webkit-scrollbar {
          display: none; /* Chrome, Safari */
        }

        .testimonial-card {
          flex: 0 0 85%;
          scroll-snap-align: start;
        }

        /* Enhanced scroll behavior */
        html {
          scroll-behavior: smooth !important;
        }
        
        /* Better focus states for mobile */
        @media (max-width: 768px) {
          .mantine-Button-root:not(footer .mantine-Button-root):not(footer *):focus,
          .mantine-Card-root:not(footer .mantine-Card-root):not(footer *):focus,
          .mantine-Anchor-root:not(footer .mantine-Anchor-root):not(footer *):focus {
            outline: 2px solid ${theme.colors.blue[6]} !important;
            outline-offset: 2px !important;
          }
        }
      `}</style>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          variant="filled"
          color="gold"
          size="lg"
          radius="50%"
          className="floating-scroll-button"
          p="sm"
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
            width: 56,
            height: 56,
            boxShadow: theme.shadows.lg,
            transform: showScrollTop ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0)',
            opacity: showScrollTop ? 1 : 0,
            transition: 'all 0.3s ease'
          }}
        >
          <IconArrowUp size={24} />
        </Button>
      )}
      </Box>
    </>
  );
} 